(ns common.schema.entities.view
  (:require
   [common.schema.schema-utils :as su]
   [clojure.set :as set]
   [clojure.walk :as walk]))

(defn temp-id?
  [eid]
  (string? eid))

(defn replace-ids
  "TODO: not tested thoroughly and may not work as expected

   Should walk through a data structure and replace any occurences of old id with the new id

   Useful when cloning and creating views, where old card ids need to be replaced with new ids"
  [data new-ids-by-old-id-map]
  (let [old-ids-set (->> new-ids-by-old-id-map
                         keys
                         set)
        replace-fn (fn [item]
                     (if (contains? old-ids-set item)
                       (get new-ids-by-old-id-map item)
                       item))]
    (walk/postwalk replace-fn data)))

(defn temp-view?
  [entity]
  (and (= (:entity/type entity) :entity.type/view)
       (temp-id? (:db/id entity))))

(defn real-view?
  [entity]
  (and (= (:entity/type entity) :entity.type/view)
       (int? (:db/id entity))))

(defn select-create-fields
  [view-entity]
  (-> view-entity
      (select-keys
       [:view/name
        :view/type
        :view/entity-type
        :view/cards
        :view/description
        :view/access-level
        :view/read-access
        :view/write-access
        :view/pipeline
        :view/configuration])))

(defn select-update-fields
  [view-entity]
  (-> view-entity
      (select-keys
       [:view/name
        :view/description
        :view/cards
        :view/configuration
        :view/access-level
        :view/read-access
        :view/write-access])))

(defn private-view?
  [view]
  (let [access-level (:view/access-level view)]
    (or (= access-level :view.access-level/private)
        (not access-level))))

(defn restricted-view?
  [view]
  (= (:view/access-level view) :view.access-level/restricted))

(defn all-users-view?
  [view]
  (let [access-level (:view/access-level view)]
    (or (= access-level :view.access-level/all-users)
        ;; everyone was old name, may be somewhere
        (= access-level :view.access-level/everyone))))

(defn view-owner?
  [account view]
  (let [view-owner (:common/owner view)
        view-owner-id (or (:db/id view-owner)
                          view-owner)
        account-id (or (:db/id account) account)]
    (= account-id view-owner-id)))

;; Only view owner can change permissions for now. This may change later
(defn has-share-access?
  [account _ view]
  (view-owner? account view))

(defn has-write-access?
  [account account-current-permissions view]
  (let [account-id (or (:db/id account) account)
        account-teams (-> account-current-permissions
                          :account-team-memberships
                          (set))
        write-access-ids (->> view
                              :view/write-access
                              (mapv (fn [entity-or-id]
                                      (or (:db/id entity-or-id)
                                          entity-or-id))))]

    (cond
      (private-view? view) (view-owner? account view)
      (restricted-view? view) (or (view-owner? account view)
                                  (some (set/union #{account-id} account-teams) write-access-ids))
      (all-users-view? view) true)))

(defn has-read-access?
  [account account-current-permissions view]
  (let [account-id (or (:db/id account) account)
        account-teams (-> account-current-permissions
                          :account-team-memberships
                          (set))
        read-access-ids (->> view
                             :view/read-access
                             (mapv (fn [entity-or-id]
                                     (or (:db/id entity-or-id)
                                         entity-or-id))))]
    (cond
      (private-view? view) (view-owner? account view)
      (restricted-view? view) (or (view-owner? account view)
                                  (some (set/union #{account-id} account-teams) read-access-ids)
                                  (has-write-access? account account-current-permissions view))
      (all-users-view? view) true)))

(def permission-fields
  [:view/access-level
   :view/read-access
   :view/write-access
   ;; TODO: Share access may be enabled later
   #_:view/share-access])

(def updatable-fields
  (vec
   (concat
    [:view/name
     :view/description
     :view/cards
     :view/configuration]
    permission-fields)))

(defn select-permission-fields
  [view-entity]
  (select-keys view-entity permission-fields))

(defn select-updatable-fields
  [view-entity]
  (select-keys view-entity updatable-fields))

(def view-entity-type-options
  (->> [:entity.type/oppo
        :entity.type/sales-opportunity
        :entity.type/exit-opportunity
        :entity.type/fundraising-opportunity
        :entity.type/person
        :entity.type/company
        :entity.type/task
        :entity.type/investor
        :entity.type/real-estate
        :entity.type/fund
        :entity.type/asset
        :entity.type/fof-asset
        :entity.type/other-asset
        :entity.type/holding
        :entity.type/calculated-indirect-holding
        :entity.type/fund-commitment
        :entity.type/kyc-profile
        :entity.type/screening-profile]
       (mapv (fn [entity-type]
               {:key   entity-type
                :label ""}))))

(def view-type-options
  [{:key   :view.type/single-entity
    :label "Single entity"}
   {:key   :view.type/multi-entity
    :label "Multi entity"}])

(def access-level-options
  [{:key   :view.access-level/private
    :label "Private"}
   {:key   :view.access-level/restricted
    :label "Restricted"}
   {:key   :view.access-level/all-users
    :label "All users"}])

(def schema
  (vec
   (concat
    (su/text-simple-field :view/name
                          "Name"
                          {:name-attribute           :view/name
                           :lower-case-str?          true
                           :not-clearable?           true
                           :mandatory-config         :mandatory/always
                           :system-required?         true
                           :system-semantics
                           su/user-provided-system-required-field-semantics
                           :editor-type              :editor.type/text-simple
                           :default-field-visibility :in-use})

    (su/text-multiline-field :view/description
                             "Description"
                             {:default-field-visibility :in-use
                              :authorization su/access-user-user})

    (su/keyword-field :view/type
                      "View type"
                      view-type-options
                      {:default-field-visibility :in-use
                       :authorization su/access-user-user})

    (su/multi-ref-field :view/pipeline
                        "View pipeline"
                        [:entity.type/pipeline]
                        {:default-field-visibility :in-use
                         :authorization    su/access-user-user
                         :option-filter-fn (fn []
                                               ; TODO
                                             )})

    (su/keyword-field :view/access-level
                      "Access level"
                      access-level-options
                      {:default-field-visibility :in-use
                       :authorization su/access-user-user})

    (su/multi-ref-field :view/read-access
                        "Read access"
                        [:entity.type/account :entity.type/team]
                        {:default-field-visibility :in-use})

    (su/multi-ref-field :view/write-access
                        "Write access"
                        [:entity.type/account :entity.type/team]
                        {:default-field-visibility :in-use})

    (su/multi-ref-field :view/share-access
                        "Share access"
                        [:entity.type/account :entity.type/team]
                        {:default-field-visibility :in-use})

    (su/keyword-field :view/entity-type
                      "View entity type"
                      view-entity-type-options
                      {:default-field-visibility :in-use})

    ;; Only for single entity (nev) views
    (su/multi-ref-field :view/cards
                        "Cards"
                        [:entity.type/card]
                        {:default-field-visibility :in-use})

    (su/text-edn-field :view/configuration
                       "View configuration"
                       {:default-field-visibility :in-use}))))
